import { useDirection, useProperty, useSnowplowTracking, useTranslation } from '@eti/providers';
import { Inline, LinkButton, Stack, Text } from '@etg/wings';
import { useState } from 'react';
import { useAuth } from '@eti/authentication';
import { Share } from '@phosphor-icons/react';
import { PartOfExternalFlights } from '@eti/schema-types';
import { Notification, variants } from '@eti/components';
import type { MouseEvent } from 'react';
import TopicsContentWrapper from '../TopicsContentWrapper';
import ReceiptPrintDialog from '../../../../receipt/components/modals/ReceiptPrintDialog';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import { getTravelDocUrl } from '../../../utils';
import FaqSection from '../../common/FaqSection';
import Receipt from '../../overview/payment/Receipt';

const ReceiptAndBookingConfirmation = () => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { authenticatedAction } = useAuth();
  const { isRTL } = useDirection();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const {
    addOnOrders,
    orderNumber,
    orderReferenceSerialized,
    partOfExternalFlights,
    purchaseDate,
    travelDocUrl,
    faqSectionPerSlug,
  } = useOrderDetails();
  const faqSectionSlug = p('ContactUs.ReceiptBookingConfirmation.Faq.Slug');
  const [isCreateInvoiceDialogOpen, setCreateInvoiceDialogOpen] = useState(false);
  const actualTravelDocUrl = getTravelDocUrl(travelDocUrl, isRTL);

  const handleOpenCreateInvoiceDialog = () => {
    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Contact',
        action: 'Click',
        label: 'CREATE_INVOICE',
        property: 'ETICKET_AND_RECEIPT',
      });
    }
    setCreateInvoiceDialogOpen(true);
  };

  const handleShowTravelDocument = (event: MouseEvent) => {
    event.preventDefault();

    if (isSnowplowTrackingEnabled) {
      sendTrackEvent({
        category: 'Contact',
        action: 'Click',
        label: 'TRAVEL_DOCUMENT',
        property: 'ETICKET_AND_RECEIPT',
      });
    }
    authenticatedAction(() => window.open(actualTravelDocUrl, '_blank'));
  };

  return (
    <>
      <TopicsContentWrapper
        chatType="oriana"
        leftColumnContent={
          <Stack spacing={24}>
            <Stack>
              <Text>{t('ContactUs.Subtopic.BookingConfirmation.Content')}</Text>
              <a
                aria-label={`${t(
                  'ContactUs.Subtopic.BookingDetails.TravelDocument.Link.Text',
                )}, ${t('ScreenReader.Link.OpensInNewTab.AriaLabel.Text')}`}
                data-testid="contact-us-travel-document-button"
                href={actualTravelDocUrl}
                onClick={handleShowTravelDocument}
                rel="noreferrer"
                target="_blank"
              >
                <Inline spacing={8}>
                  {t('ContactUs.Subtopic.BookingDetails.TravelDocument.Link.Text')}
                  <Share aria-hidden="true" mirrored={isRTL} size={20} />
                </Inline>
              </a>
              <LinkButton
                data-testid="contact-us-create-invoice-button"
                onClick={handleOpenCreateInvoiceDialog}
              >
                {t('OrderDetails.Payment.Overview.CreateInvoiceButton.Label')}
              </LinkButton>
              <Receipt />
            </Stack>
            {faqSectionPerSlug?.[faqSectionSlug] && (
              <FaqSection
                headingAs="h5"
                headingLevel={4}
                padding={0}
                section={faqSectionPerSlug[faqSectionSlug]}
              />
            )}
          </Stack>
        }
        rightColumnContent={
          partOfExternalFlights === PartOfExternalFlights.All && (
            <Notification
              dataTestId="receipt-and-booking-confirmation-no-contact-us-notification"
              variant={variants.WARNING}
            >
              <Text asHtml>{t('ContactUs.NoContact.Notification.Content')}</Text>
            </Notification>
          )
        }
        shouldShowContactMethods={partOfExternalFlights !== PartOfExternalFlights.All}
        snowplowProperty="ETICKET_AND_RECEIPT"
      />
      <ReceiptPrintDialog
        addOnOrders={addOnOrders}
        isOpen={isCreateInvoiceDialogOpen}
        onClose={() => setCreateInvoiceDialogOpen(false)}
        orderNumber={orderNumber}
        orderReferenceSerialized={orderReferenceSerialized}
        purchaseDate={purchaseDate}
        shouldShowPrintConfirmationButton={false}
      />
    </>
  );
};

export default ReceiptAndBookingConfirmation;
